import React, { useCallback, useState } from 'react';

import type { Moment } from 'moment';
import {
  MdDeleteForever,
  MdEdit,
  MdEvent,
  MdFileCopy,
  MdKeyboardArrowDown,
  MdKeyboardArrowUp,
} from 'react-icons/md';
import { FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { setDeleteRecurrentEventInfo } from '@modules/calendar/duck';
import { setEventToDelete } from '@modules/events/duck/slice';
import { featureFlags } from '@modules/featureFlags/utils/featureFlags';
import { Button, Dropdown, Menu } from '@shared/components';
import { offlineSel } from '@shared/duck/selectors/general.selectors';

import { goToEventsCreate, goToEventsEdit } from '../../duck/actions';
import styles from './EventActionsMenu.less';

const { Item: MenuItem } = Menu;

interface Props {
  eventId: number | string;
  eventName: string;
  recurrentId: number;
  hideDeleteSeries: boolean;
  fromDate: Moment;
}

const EventActionsMenu: React.FC<Props> = ({
  eventId,
  recurrentId,
  eventName,
  hideDeleteSeries,
  fromDate,
}) => {
  const dispatch = useDispatch();
  const [isVisible, setIsVisible] = useState(false);
  const isOffline = useSelector(offlineSel);

  const handleVisibleChange = useCallback(
    visible => {
      setIsVisible(visible);
    },
    [setIsVisible],
  );

  const handleItemClick = useCallback(
    (clone?: boolean | undefined, recurringEventId?: number) =>
      (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
        setIsVisible(false);
        dispatch(goToEventsEdit({ eventId, clone, recurringEventId }));
        e.stopPropagation();
      },
    [dispatch, eventId, setIsVisible],
  );

  const handleDeleteClick = useCallback(
    () => (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
      setIsVisible(false);
      dispatch(setEventToDelete({ id: +eventId, name: eventName }));
      e.stopPropagation();
    },
    [dispatch, eventId, eventName, setIsVisible],
  );

  const handleDeleteRecurrenceClick = useCallback(
    () => (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
      setIsVisible(false);
      dispatch(
        setDeleteRecurrentEventInfo({
          modalVisible: true,
          eventId: eventId as number,
          recurrentId,
          dateFrom: fromDate.toDate(),
          defaultDate: fromDate,
        }),
      );
      e.stopPropagation();
    },
    [dispatch, eventId, recurrentId, fromDate],
  );

  const handleCreateSeries = useCallback(
    e => {
      e.stopPropagation();
      dispatch(
        goToEventsCreate({
          query: {
            seriesFromEventId: eventId,
            recurrentId,
          },
        }),
      );
      setIsVisible(false);
    },
    [dispatch, eventId, recurrentId],
  );

  const onClickMenuIcon = (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
    e.stopPropagation();
  };

  return (
    <div className={styles.menuContainer}>
      <Button
        block
        size="large"
        color="info"
        uppercase={false}
        shape="round"
        className={styles.leftBtn}
        onClick={handleItemClick()}
      >
        <MdEdit className={styles.btnIcon} />
        <FormattedMessage id="page.editEvent" />
      </Button>
      <Dropdown
        onVisibleChange={handleVisibleChange}
        visible={isVisible}
        overlay={
          <Menu className={styles.actionsMenu}>
            {!!recurrentId && (
              <MenuItem>
                <Button
                  disabled={isOffline}
                  onClick={handleItemClick(false, recurrentId)}
                  type="link"
                  size="small"
                  block
                >
                  <MdEdit />
                  <FormattedMessage id="events.editSeries" />
                </Button>
              </MenuItem>
            )}

            <MenuItem>
              <Button
                disabled={isOffline}
                onClick={handleItemClick(true)}
                type="link"
                size="small"
                block
              >
                <MdFileCopy /> <FormattedMessage id="events.duplicateEvent" />
              </Button>
            </MenuItem>
            <MenuItem>
              <Button
                disabled={isOffline}
                onClick={handleDeleteClick()}
                type="link"
                size="small"
                block
              >
                <MdDeleteForever /> <FormattedMessage id="events.deleteEvent" />
              </Button>
            </MenuItem>
            {featureFlags.getFlag('SBL_4519_SERIES_FROM_EVENT') ? (
              <MenuItem>
                <Button
                  disabled={isOffline}
                  onClick={handleCreateSeries}
                  type="link"
                  size="small"
                  block
                >
                  <MdEvent />
                  <FormattedMessage id="events.seriesFromEvent" />
                </Button>
              </MenuItem>
            ) : undefined}
            {!!(recurrentId && !hideDeleteSeries) && (
              <MenuItem>
                <Button
                  disabled={isOffline}
                  onClick={handleDeleteRecurrenceClick()}
                  type="link"
                  size="small"
                  block
                >
                  <MdDeleteForever />
                  <FormattedMessage id="events.deleteSeries" />
                </Button>
              </MenuItem>
            )}
          </Menu>
        }
        trigger={['click']}
        placement="bottomRight"
        // @ts-expect-error: missing native onClick event
        onClick={onClickMenuIcon}
      >
        <Button
          size="large"
          color="info"
          uppercase={false}
          shape="round"
          className={styles.rightBtn}
        >
          {isVisible ? <MdKeyboardArrowUp /> : <MdKeyboardArrowDown />}
        </Button>
      </Dropdown>
    </div>
  );
};

export default EventActionsMenu;
